function loadDeferredScript(url, marker) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-deferred-script="${marker}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') resolve();
      else existing.addEventListener('load', resolve, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = url;
    script.defer = true;
    script.dataset.deferredScript = marker;
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true';
        resolve();
      },
      { once: true }
    );
    script.addEventListener('error', reject, { once: true });
    document.head.appendChild(script);
  });
}

window.ensureCartDrawerScripts = function ensureCartDrawerScripts() {
  if (window.__cartDrawerScriptsPromise) return window.__cartDrawerScriptsPromise;

  const drawer = document.querySelector('cart-drawer');
  const cartJs = drawer?.dataset.cartJs;
  const quantityPopoverJs = drawer?.dataset.quantityPopoverJs;

  if (!cartJs) {
    window.__cartDrawerScriptsPromise = Promise.resolve();
    return window.__cartDrawerScriptsPromise;
  }

  window.__cartDrawerScriptsPromise = loadDeferredScript(cartJs, 'cart-js').then(() => {
    if (quantityPopoverJs) {
      return loadDeferredScript(quantityPopoverJs, 'quantity-popover-js');
    }
  });

  return window.__cartDrawerScriptsPromise;
};

function getCartItemsScroller(shell, items) {
  if (items.scrollHeight - items.clientHeight > 1) return items;
  if (shell.scrollHeight - shell.clientHeight > 1) return shell;
  return items;
}

function updateCartDrawerScrollState() {
  const shell = document.getElementById('CartDrawer-ItemsShell');
  const items = shell?.querySelector('cart-drawer-items');
  if (!shell || !items) return;

  const scroller = getCartItemsScroller(shell, items);
  const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  const scrollable = maxScroll > 1;
  let atBottom = !scrollable || scroller.scrollTop >= maxScroll - 2;

  const lastItem = items.querySelector('.cart-item:last-child');
  if (scrollable && lastItem) {
    const scrollerRect = scroller.getBoundingClientRect();
    const lastRect = lastItem.getBoundingClientRect();
    if (lastRect.bottom <= scrollerRect.bottom + 2) {
      atBottom = true;
    }
  }

  shell.classList.toggle('is-scrollable', scrollable);
  shell.classList.toggle('is-at-bottom', atBottom);

  const hint = document.getElementById('CartDrawer-ScrollHint');
  if (hint) {
    hint.hidden = !scrollable || atBottom;
    hint.setAttribute('aria-hidden', !scrollable || atBottom ? 'true' : 'false');
  }
}

function bindCartDrawerItemsScroll() {
  const shell = document.getElementById('CartDrawer-ItemsShell');
  const items = shell?.querySelector('cart-drawer-items');
  if (!shell || !items) return;

  if (items === shell.__scrollItemsBound) {
    updateCartDrawerScrollState();
    return;
  }

  if (shell.__scrollItemsBound && shell.__scrollOnScroll) {
    shell.__scrollItemsBound.removeEventListener('scroll', shell.__scrollOnScroll);
    shell.removeEventListener('scroll', shell.__scrollOnScroll);
  }
  shell.__scrollRO?.disconnect();
  shell.__lastItemIO?.disconnect();

  const onScroll = () => updateCartDrawerScrollState();
  shell.__scrollOnScroll = onScroll;
  shell.__scrollItemsBound = items;

  items.addEventListener('scroll', onScroll, { passive: true });
  shell.addEventListener('scroll', onScroll, { passive: true });

  shell.__scrollRO = new ResizeObserver(() => {
    updateCartDrawerScrollState();
    bindCartDrawerLastItemObserver();
  });
  shell.__scrollRO.observe(items);

  bindCartDrawerLastItemObserver();
  updateCartDrawerScrollState();
}

function bindCartDrawerLastItemObserver() {
  const shell = document.getElementById('CartDrawer-ItemsShell');
  const items = shell?.querySelector('cart-drawer-items');
  if (!shell || !items) return;

  const lastItem = items.querySelector('.cart-item:last-child');
  shell.__lastItemIO?.disconnect();
  if (!lastItem) return;

  const scroller = getCartItemsScroller(shell, items);
  shell.__lastItemIO = new IntersectionObserver(
    () => updateCartDrawerScrollState(),
    { root: scroller, threshold: [0, 0.5, 0.85, 1] }
  );
  shell.__lastItemIO.observe(lastItem);
}

window.updateCartDrawerScrollState = updateCartDrawerScrollState;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    bindCartDrawerItemsScroll();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    const prefetchScripts = () => {
      if (typeof window.ensureCartDrawerScripts === 'function') {
        window.ensureCartDrawerScripts();
      }
    };
    cartLink.addEventListener('pointerenter', prefetchScripts, { once: true, passive: true });
    cartLink.addEventListener('focus', prefetchScripts, { once: true, passive: true });

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  loadRecommendations() {
    if (window.__cartRecJS) return;
    const url = this.dataset.recommendationsUrl;
    if (!url || !this.querySelector('#cart-recommendations')) return;
    window.__cartRecJS = true;
    const script = document.createElement('script');
    script.src = url;
    script.defer = true;
    document.head.appendChild(script);
  }

  open(triggeredBy) {
    const revealDrawer = () => {
      this.loadRecommendations();
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );

      document.body.classList.add('overflow-hidden');
      requestAnimationFrame(() => {
        updateCartDrawerScrollState();
        window.setTimeout(updateCartDrawerScrollState, 120);
      });
    };

    if (typeof window.ensureCartDrawerScripts === 'function') {
      window.ensureCartDrawerScripts().then(revealDrawer).catch(revealDrawer);
    } else {
      revealDrawer();
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const updateDrawer = () => {
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
        this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });

      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        bindCartDrawerItemsScroll();
        updateCartDrawerScrollState();
        this.open();
      });
    };

    if (typeof window.ensureCartDrawerScripts === 'function') {
      window.ensureCartDrawerScripts().then(updateDrawer).catch(updateDrawer);
    } else {
      updateDrawer();
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    requestAnimationFrame(() => {
      bindCartDrawerItemsScroll();
      updateCartDrawerScrollState();
    });
  });
}
